import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, X, Search, Copy, Settings, Upload } from "lucide-react";

const LIB_ORDER = [
  ["fabric", "Fabrics"],
  ["zipper", "Zippers"],
  ["button", "Buttons"],
  ["label", "Labels & Tickets"],
  ["print", "Prints"],
  ["bulk_colorway", "Bulk Colorways"],
];

function FieldInput({ def, value, onChange }) {
  const cls = "w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950";
  const [customMode, setCustomMode] = useState(false);
  const [multiDraft, setMultiDraft] = useState("");
  const isEnum = def.type === "enum";

  // If existing value isn't in options and isn't empty, treat as custom
  useEffect(() => {
    if (isEnum && value && !customMode && def.options && !def.options.includes(value)) setCustomMode(true);
  }, [isEnum, value, def.options, customMode]);

  if (def.type === "multi_enum") {
    const arr = Array.isArray(value) ? value : (value ? [value] : []);
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    const addCustom = () => {
      const v = multiDraft.trim();
      if (!v) return;
      if (!arr.some((x) => x.toLowerCase() === v.toLowerCase())) onChange([...arr, v]);
      setMultiDraft("");
    };
    const custom = arr.filter((o) => !(def.options || []).includes(o));
    return (
      <div className="border border-zinc-300 rounded-md p-2">
        <div className="flex flex-wrap gap-1.5">
          {(def.options || []).map((o) => (
            <button type="button" key={o} onClick={() => toggle(o)}
              className={`px-2 py-1 rounded text-[11px] border transition-colors ${arr.includes(o) ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-600 hover:bg-zinc-100"}`}>{o}</button>
          ))}
          {custom.map((o) => (
            <button type="button" key={o} onClick={() => toggle(o)}
              className="px-2 py-1 rounded text-[11px] border bg-zinc-950 text-white border-zinc-950">{o} ✕</button>
          ))}
          <input value={multiDraft} onChange={(e) => setMultiDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
            placeholder="Other…" className="w-24 border border-zinc-200 rounded px-1.5 py-0.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-zinc-950" />
        </div>
        <p className="text-[10px] text-zinc-400 mt-1.5">Leave empty to mean "suitable for all".</p>
      </div>
    );
  }

  if (isEnum) {
    if (customMode) {
      return (
        <div className="flex gap-1">
          <input value={value ?? ""} placeholder="Custom value…" onChange={(e) => onChange(e.target.value)} className={cls} />
          <button type="button" onClick={() => { setCustomMode(false); onChange(""); }} title="Back to list"
            className="text-[10px] text-zinc-500 hover:text-zinc-950 border border-zinc-300 rounded-md px-2">← list</button>
        </div>
      );
    }
    return (
      <select value={value ?? ""} onChange={(e) => {
        if (e.target.value === "__other__") { setCustomMode(true); onChange(""); }
        else onChange(e.target.value);
      }} className={cls + " bg-white"}>
        <option value="">—</option>
        {def.options.map((o) => <option key={o} value={o}>{o}</option>)}
        <option value="__other__">Other…</option>
      </select>
    );
  }
  if (def.type === "number")
    return <input type="number" step="0.01" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : parseFloat(e.target.value))} className={cls} />;
  if (def.type === "boolean")
    return <label className="flex items-center gap-2 h-9"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} /><span className="text-xs text-zinc-500">{value ? "Yes" : "No"}</span></label>;
  if (def.type === "color")
    return <div className="flex gap-2 items-center"><input type="color" value={value || "#cccccc"} onChange={(e) => onChange(e.target.value)} className="h-9 w-14 rounded cursor-pointer" /><input value={value || ""} placeholder="#hex" onChange={(e) => onChange(e.target.value)} className={cls} /></div>;
  if (def.type === "image_url") {
    const upload = async (file) => {
      if (!file) return;
      const rd = new FileReader();
      rd.onload = async () => {
        try {
          const { data } = await api.post("/materials", { name: file.name.replace(/\.[^.]+$/, ""), type: "fabric", image_b64: rd.result });
          onChange(data.image_url);
          toast.success("Swatch uploaded");
        } catch (e) { toast.error(apiError(e)); }
      };
      rd.readAsDataURL(file);
    };
    return (
      <div className="flex gap-2 items-center">
        {value ? <img src={value} alt="swatch" className="h-10 w-10 rounded border border-zinc-200 object-cover" /> : <div className="h-10 w-10 rounded bg-zinc-100 border border-zinc-200" />}
        <label className="inline-flex items-center gap-1 border border-zinc-300 rounded-md px-3 py-2 text-xs cursor-pointer hover:bg-zinc-100">
          <Upload size={12} /> {value ? "Replace" : "Upload"}
          <input type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} className="hidden" />
        </label>
        {value && <button type="button" onClick={() => onChange("")} className="text-[10px] text-red-500 hover:underline">Remove</button>}
      </div>
    );
  }
  if (def.type === "textarea")
    return <textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={cls} />;
  return <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={cls} />;
}

function ItemEditor({ schema, item, onSave, onCancel, onDelete, onDuplicate, saving, canDelete }) {
  const [attrs, setAttrs] = useState(item.attributes || {});
  const [name, setName] = useState(item.name || "");

  const setAttr = (k, v) => setAttrs({ ...attrs, [k]: v });

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/60 flex items-center justify-center p-4" data-testid="library-editor">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">{item.id ? "Edit" : "New"} {schema.label}</h2>
          <button onClick={onCancel} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"><X size={16} /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-6">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Display Name</label>
            <input data-testid="library-name" value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" placeholder="Optional fallback if attributes are blank" />
          </div>
          {Object.entries(schema.sections).map(([section, fields]) => (
            <div key={section}>
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium mb-2">{section}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(fields).map(([k, def]) => (
                  <div key={k}>
                    <label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-medium">{def.label || k}{def.unit ? ` (${def.unit})` : ""}{def.required && <span className="text-red-500"> *</span>}</label>
                    <FieldInput def={def} value={attrs[k]} onChange={(v) => setAttr(k, v)} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">{canDelete && item.id && (
            <button data-testid="library-delete" onClick={() => onDelete(item)} className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:bg-red-50 border border-red-200 rounded-md px-3 py-1.5">
              <Trash2 size={12} /> Delete
            </button>
          )}
          {item.id && (
            <button data-testid="library-duplicate" onClick={() => onDuplicate({ name: name ? `${name} (copy)` : "", attributes: attrs })}
              className="inline-flex items-center gap-1.5 text-xs text-zinc-600 hover:bg-zinc-100 border border-zinc-300 rounded-md px-3 py-1.5">
              <Copy size={12} /> Duplicate
            </button>
          )}</div>
          <div className="flex gap-2">
            <button onClick={onCancel} className="text-sm px-4 py-2 rounded-md hover:bg-zinc-100">Cancel</button>
            <button data-testid="library-save" disabled={saving} onClick={() => onSave({ name, attributes: attrs })}
              className="bg-zinc-950 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-800 disabled:opacity-40">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ManageOptionsModal({ libraryType, schema, onClose, onSaved }) {
  // Flatten enum fields from schema for this library
  const enumFields = useMemo(() => {
    const out = [];
    for (const [section, fields] of Object.entries(schema.sections || {})) {
      for (const [key, def] of Object.entries(fields)) {
        if (def.type === "enum" || def.type === "multi_enum") out.push({ section, key, label: def.label || key, options: def.options || [] });
      }
    }
    return out;
  }, [schema]);

  const [state, setState] = useState(() => enumFields.reduce((acc, f) => (acc[`${f.section}||${f.key}`] = [...(f.options)], acc), {}));
  const [savingKey, setSavingKey] = useState("");
  const [pendingInput, setPendingInput] = useState({});

  const key = (f) => `${f.section}||${f.key}`;

  const addOption = (f) => {
    const val = (pendingInput[key(f)] || "").trim();
    if (!val) return;
    if (state[key(f)].map((o) => o.toLowerCase()).includes(val.toLowerCase())) { toast.error("Already in list"); return; }
    setState({ ...state, [key(f)]: [...state[key(f)], val] });
    setPendingInput({ ...pendingInput, [key(f)]: "" });
  };
  const removeOption = (f, opt) => setState({ ...state, [key(f)]: state[key(f)].filter((o) => o !== opt) });
  const resetOption = (f) => setState({ ...state, [key(f)]: [] });

  const saveField = async (f) => {
    setSavingKey(key(f));
    try {
      await api.put(`/libraries/${libraryType}/enum-options`, { section: f.section, field: f.key, options: state[key(f)] });
      toast.success(`Saved '${f.label}'`);
      onSaved?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingKey(""); }
  };

  return (
    <div className="fixed inset-0 z-[55] bg-zinc-950/60 flex items-center justify-center p-4" data-testid="library-manage-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg font-bold">Manage dropdown options — {schema.label}</h2>
            <p className="text-xs text-zinc-500 mt-0.5">Empty list restores the built-in defaults. Options apply team-wide.</p>
          </div>
          <button data-testid="library-manage-close" onClick={onClose} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"><X size={16} /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-5">
          {enumFields.map((f) => {
            const opts = state[key(f)] || [];
            const busy = savingKey === key(f);
            return (
              <div key={key(f)} className="border border-zinc-200 rounded-lg p-3" data-testid={`manage-field-${f.key}`}>
                <div className="flex items-baseline justify-between">
                  <div>
                    <p className="text-sm font-medium">{f.label}</p>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">{f.section}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => resetOption(f)} className="text-[10px] text-zinc-500 hover:underline">Reset</button>
                    <button data-testid={`manage-save-${f.key}`} disabled={busy} onClick={() => saveField(f)}
                      className="text-xs bg-zinc-950 text-white px-3 py-1 rounded hover:bg-zinc-800 disabled:opacity-40">{busy ? "Saving…" : "Save"}</button>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {opts.length === 0 && <span className="text-[11px] text-zinc-400">List is empty — defaults will be used.</span>}
                  {opts.map((o) => (
                    <span key={o} className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-800 text-xs px-2 py-1 rounded-md">
                      {o}
                      <button onClick={() => removeOption(f, o)} className="text-zinc-400 hover:text-red-600"><X size={10} /></button>
                    </span>
                  ))}
                </div>
                <div className="mt-2 flex gap-1">
                  <input value={pendingInput[key(f)] || ""} onChange={(e) => setPendingInput({ ...pendingInput, [key(f)]: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(f); } }}
                    placeholder="Add option…" data-testid={`manage-input-${f.key}`}
                    className="flex-1 border border-zinc-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                  <button data-testid={`manage-add-${f.key}`} onClick={() => addOption(f)}
                    className="text-xs border border-zinc-300 rounded-md px-2 py-1 hover:bg-zinc-100 inline-flex items-center gap-1"><Plus size={10} /> Add</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


export default function LibrariesPage() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "materials_admin";
  const [schemas, setSchemas] = useState({});
  const [tab, setTab] = useState("fabric");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editorKey, setEditorKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [manageOpen, setManageOpen] = useState(false);

  const reloadSchemas = () => api.get("/libraries/schemas").then((r) => setSchemas(r.data));
  useEffect(() => { reloadSchemas(); }, []);
  useEffect(() => {
    if (!schemas[tab]) return;
    api.get(`/libraries/${tab}/items`).then((r) => setItems(r.data));
  }, [tab, schemas]);

  const schema = schemas[tab];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => JSON.stringify(it.attributes || {}).toLowerCase().includes(q) || (it.display?.name || "").toLowerCase().includes(q));
  }, [items, query]);

  const openNew = () => { setEditing({ attributes: {}, name: "" }); setEditorKey((k) => k + 1); };
  const openEdit = (it) => { setEditing({ ...it }); setEditorKey((k) => k + 1); };

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editing.id) {
        const { data } = await api.patch(`/libraries/items/${editing.id}`, payload);
        setItems(items.map((it) => it.id === data.id ? data : it));
        toast.success("Updated");
      } else {
        const { data } = await api.post(`/libraries/${tab}/items`, { library_type: tab, ...payload });
        setItems([data, ...items]);
        toast.success("Created");
      }
      setEditing(null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete this ${schema.label}?`)) return;
    try {
      await api.delete(`/libraries/items/${it.id}`);
      setItems(items.filter((x) => x.id !== it.id));
      setEditing(null);
      toast.success("Deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  const duplicate = (payload) => {
    // Convert current editor state into a fresh (un-saved) new item.
    // Bump editorKey so ItemEditor re-mounts with the new initial state.
    setEditing({ attributes: payload.attributes || {}, name: payload.name || "" });
    setEditorKey((k) => k + 1);
    toast.info("Duplicated — save to create a new item");
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Master data</p>
        <h1 className="font-heading text-4xl font-bold mt-1 tracking-tight" data-testid="libraries-title">Material Libraries</h1>
        <p className="text-zinc-500 mt-2 text-sm max-w-xl">Fabrics, zippers, buttons, labels & price tickets — the master data used to build your BOMs and drive product techpacks.</p>
      </div>

      <div className="mt-8 border-b border-zinc-200 flex gap-1 overflow-x-auto">
        {LIB_ORDER.map(([k, label]) => schemas[k] && (
          <button key={k} data-testid={`library-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === k ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-950"}`}>
            {label}
          </button>
        ))}
      </div>

      {schema && (
        <>
          <div className="mt-6 flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search size={14} className="absolute left-3 top-3 text-zinc-400" />
              <input data-testid="library-search" placeholder={`Search ${schema.label.toLowerCase()}s…`} value={query} onChange={(e) => setQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
            </div>
            <button data-testid="library-add" onClick={openNew} className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-800">
              <Plus size={14} /> New {schema.label}
            </button>
            {canManage && (
              <button data-testid="library-manage-options" onClick={() => setManageOpen(true)}
                className="inline-flex items-center gap-2 border border-zinc-300 rounded-md px-3 py-2 text-sm font-medium hover:bg-zinc-100">
                <Settings size={14} /> Manage options
              </button>
            )}
          </div>

          {schema.note && <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">{schema.note}</p>}

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.length === 0 && (
              <div className="col-span-full border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400 text-sm">
                No {schema.label.toLowerCase()}s yet. Click <span className="text-zinc-600 font-medium">New {schema.label}</span> to add one.
              </div>
            )}
            {filtered.map((it) => (
              <button key={it.id} data-testid={`library-card-${it.id}`} onClick={() => openEdit(it)}
                className="text-left border border-zinc-200 bg-white rounded-lg p-4 hover:border-zinc-400 transition-colors">
                <div className="flex items-start gap-3">
                  {it.display?.hex ? (
                    <span className="w-10 h-10 rounded border border-zinc-200 shrink-0" style={{ backgroundColor: it.display.hex }} />
                  ) : (
                    <span className="w-10 h-10 rounded bg-zinc-100 border border-zinc-200 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{it.display?.name}</p>
                    {it.display?.subtitle && <p className="text-xs text-zinc-500 truncate mt-0.5">{it.display.subtitle}</p>}
                    {it.attributes?.supplier_name && <p className="text-[11px] text-zinc-400 mt-1">{it.attributes.supplier_name}</p>}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {editing && schema && (
        <ItemEditor key={editorKey} schema={schema} item={editing} saving={saving} canDelete={true}
          onSave={save} onCancel={() => setEditing(null)} onDelete={remove} onDuplicate={duplicate} />
      )}
      {manageOpen && schema && (
        <ManageOptionsModal libraryType={tab} schema={schema} onClose={() => setManageOpen(false)} onSaved={() => { reloadSchemas(); toast.success("Options updated"); }} />
      )}
    </div>
  );
}
